import React, {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react'

import {
  LanguageType,
  PromptGroup,
  VoiceGroupFactor,
  VoiceTagType,
} from 'service/list'
import classNames from 'classnames'
import {
  Control,
  Controller,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form'
import { contentVideoTextTypeFormValues } from 'utils/type'

import { Me } from 'service/user'
import { isMobile } from 'react-device-detect'
import { CircularCheckbox } from 'components/CircularCheckbox'
import { TypeCastVoicePlayer } from 'pages/product-video-editor/components/video/voice/TypeCastVoicePlayer'
import { SelectionModal } from 'components/layout/SelectionModal'
import { UserVoiceList } from './UserVoiceList'
import { UserVoiceDiv } from '../linkType/components/UserVoiceDiv'
import { UpdateTag } from 'components/tag/UpdateTag'
import { PlanTag } from 'components/planTag'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

interface TextTypeAiScriptDisplayProps {
  watch: UseFormWatch<contentVideoTextTypeFormValues>
  setValue: UseFormSetValue<contentVideoTextTypeFormValues>
  control: Control<contentVideoTextTypeFormValues, any>
  languageList: LanguageType[] | undefined
  voiceList: VoiceGroupFactor[] | undefined
  userVoiceList: VoiceGroupFactor[] | undefined
  userVoiceRefetch: any
  voiceRefetch: any
  userInfo: Me
  setPromptStatus?: Dispatch<SetStateAction<boolean[]>>
  voiceTags: VoiceTagType | undefined
}

export const TextTypeAiScriptDisplay = ({
  watch,
  setValue,
  control,
  languageList,
  userVoiceList,
  userVoiceRefetch,
  voiceList,
  voiceRefetch,
  userInfo,
  setPromptStatus,
  voiceTags,
}: PropsWithChildren<TextTypeAiScriptDisplayProps>) => {
  const { t } = useTranslation()
  const selectedLanguageId = watch('languageId')
  const [voicePlaying, setVoicePlaying] = useState<boolean[]>(
    new Array(voiceList?.length).fill(false)
  )
  const navigate = useNavigate()
  const voiceRefs = useRef<(HTMLAudioElement | null)[]>([])

  const [isModalOpen, setModalOpen] = useState<boolean>(false)
  const [isGenderSelectBoxOpen, setGenderSelectBoxOpen] =
    useState<boolean>(false)
  const [isAgeSelectBoxOpen, setAgeSelectBoxOpen] = useState<boolean>(false)

  const [genderWords, setGenderWords] = useState<string[]>(
    voiceTags?.genders ?? ['남성', '여성']
  )

  const [ageWords, setAgeWords] = useState<string[]>(
    voiceTags?.ages ?? ['청년', '중년', '10대']
  )

  const [languageName, setLanguageName] = useState<string>('')
  const [selectedPrompt, setSelectedPrompt] = useState<
    PromptGroup | { planName: string; introduction: string }
  >({
    planName: '',
    introduction: '',
  })

  const [voiceName, setVoiceName] = useState<string>('')
  const [genderValue, setGenderValue] = useState<string>('')
  const [ageValue, setAgeValue] = useState<string>('')
  const [voiceListValue, setVoiceListValue] = useState<VoiceGroupFactor[] | []>(
    []
  )

  const handleListModalOpen = () => {
    setModalOpen((prev) => !prev)
  }

  const handleVoicePlayButtonClick = (status: boolean, index: number) => {
    const updatedPlayingState = voicePlaying.map((value, idx) =>
      idx === index ? status : false
    )
    setVoicePlaying(updatedPlayingState)
    if (voiceRefs.current[index]) {
      if (status) {
        voiceRefs.current[index]?.play()
      } else {
        voiceRefs.current[index]?.pause()
      }
    }
  }

  const handleGenderChange = (gender: string) => {
    setGenderWords(
      (prev) =>
        prev.includes(gender)
          ? prev.filter((item) => item !== gender) // 체크 해제
          : [...prev, gender] // 체크 선택
    )
  }
  const handleAgeChange = (age: string) => {
    setAgeWords(
      (prev) =>
        prev.includes(age)
          ? prev.filter((item) => item !== age) // 체크 해제
          : [...prev, age] // 체크 선택
    )
  }

  useEffect(() => {
    voiceRefs.current.forEach((audioRef, index) => {
      if (audioRef && !voicePlaying[index]) {
        audioRef.pause()
        audioRef.currentTime = 0
      }
    })
  }, [voicePlaying])

  useEffect(() => {
    if (voiceList) {
      let filter = voiceList

      // 1차: 성별 필터링
      if (genderWords.length > 0) {
        filter = filter.filter((el) => genderWords.includes(el?.gender ?? ''))
      } else {
        filter = []
      }

      // 2차: 연령 필터링
      if (ageWords.length > 0) {
        filter = filter.filter((el) => ageWords.includes(el.age ?? ''))
      } else {
        filter = []
      }

      // 상태 업데이트
      const prevVoiceId = watch('voiceId')
      setVoiceListValue(filter)
      setVoicePlaying(new Array(filter.length).fill(false))
    }
  }, [genderWords, ageWords, voiceList])

  useEffect(() => {
    if (isModalOpen) {
      document.body.classList.add('overflow-hidden') // Tailwind 유틸리티 클래스 사용
    } else {
      document.body.classList.remove('overflow-hidden')
    }
  }, [isModalOpen])

  useEffect(() => {
    //언어를 선택하는 구조

    if (languageList) {
      const [language] = languageList?.filter(
        (el) => el?.id === watch('languageId')
      )
      setLanguageName(language?.name)
    }

    voiceRefetch()
  }, [languageList, selectedLanguageId])

  useEffect(() => {
    const selectedVoiceId = watch('voiceId')
    const selectedUserVoiceId = watch('userVoiceId')
    if (voiceList && selectedVoiceId! !== 0) {
      setVoicePlaying(new Array(voiceList?.length).fill(false))
      //voice아이디를 가지고 list에서 찾아서 gender와 age를 셋팅해야함

      const [voice] = voiceList.filter((el) => el.id === selectedVoiceId)

      if (voice) {
        setVoiceName(voice?.name)
        setAgeValue(voice.age ?? '')
        setGenderValue(voice.gender ?? '')
      } else {
        setVoiceName(voiceList[0]?.name)
        setValue('voiceId', voiceList[0]?.id)
      }
    }

    if (userVoiceList && selectedUserVoiceId! !== 0) {
      //voice아이디를 가지고 list에서 찾아서 gender와 age를 셋팅해야함

      const [voice] = userVoiceList.filter(
        (el) => el.id === selectedUserVoiceId
      )

      if (voice) {
        setVoiceName(voice?.name)
        setAgeValue(voice.age ?? '')
        setGenderValue(voice.gender ?? '')
      }
    }
  }, [
    voiceList,
    watch('voiceId'),
    watch('userVoiceId'),
    watch('languageId'),
    userVoiceList,
  ])

  return (
    <>
      <div className="bgm   mb-10">
        <div className="flex  md:flex-row flex-col md:items-center justify-between gap-3">
          <p className="font-bold text-[18px] mb-4 md:mb-0 shrink-0 w-[92px]">
            {t('contentVideoEditor.voiceSelection.voice')}
          </p>
          <p className="md:w-full  md:h-[61px] w-full rounded-[10px] bg-[#F6F8FB] px-4 py-5 flex items-center justify-between font-bold">
            <div className="flex md:flex-row flex-col md:items-center md:gap-5 gap-4">
              <p className="flex items-center text-[14px] gap-1">
                <span className="bg-[#D9DEE8] px-1 py-[2px] text-[#8F929B] rounded-[4px] flex-shrink-0 ">
                  {t('contentVideoEditor.voiceSelection.language')}
                </span>
                <span className="flex-shrink-0 ">{languageName}</span>
              </p>

              <p className="flex items-center text-[14px] gap-1">
                <span className="bg-[#D9DEE8] px-1 py-[2px] text-[#8F929B] rounded-[4px] flex-shrink-0">
                  {t('contentVideoEditor.voiceSelection.voice')}
                </span>
                <div className="flex items-center">
                  <span
                    className="font-bold text-white text-[11px] px-1 py-[2px] rounded-[4px]"
                    style={{
                      background: `linear-gradient(to right,#6095FF,#00CBB3)`,
                    }}
                  >
                    UPDATE
                  </span>
                </div>
                <span className="flex-shrink-0 ">{voiceName}</span>
              </p>
            </div>
            <button
              className="px-2 py-[6px] bg-[#2E343D] text-[white] rounded-[4px] text-[14px] flex-shrink-0"
              onClick={handleListModalOpen}
              type="button"
            >
              {t('contentVideoEditor.voiceSelection.select')} {'>'}
            </button>
          </p>
        </div>
      </div>

      <SelectionModal isModal={isModalOpen}>
        <div
          className={classNames('', {
            'bg-white w-[816px] h-auto  rounded-t-[20px] px-8 pt-8  flex flex-col   gap-8 pb-28 ':
              !isMobile,
            'bg-white  w-full    flex flex-col     gap-8 pt-10 px-4 ': isMobile,
          })}
        >
          <div className="flex flex-col   pb-40    gap-8 md:pb-0">
            <div className="ai-prompt flex flex-col items-start">
              <p className="font-bold  mb-[16px] text-[24px]">
                {t('contentVideoEditor.voiceSelection.language')}
              </p>
              <div className=" w-full  rounded-[10px] grid md:grid-cols-2 grid-cols-1 gap-y-4 ">
                {languageList?.map((language, index) => {
                  return (
                    <>
                      <div className="flex" key={`language_${index}`}>
                        <Controller
                          render={({ field }) => (
                            <>
                              <CircularCheckbox
                                {...field}
                                id={index}
                                checked={watch('languageId') === language?.id}
                                onChange={() => {
                                  setValue('languageId', language.id)
                                  setLanguageName(language?.name)
                                }}
                                label={language.name}
                              />
                            </>
                          )}
                          rules={{
                            validate: (value) => {
                              return Number(watch('languageId')) === 0
                                ? 'language ID must not be 0'
                                : true
                            },
                          }}
                          name="languageId"
                          control={control}
                        />
                      </div>
                    </>
                  )
                })}
              </div>

              <>
                <div className="flex flex-col gap-4 my-5 w-full">
                  <hr className=" border-[1px] border-gray-300  w-full" />
                  <div className="flex flex-col  gap-4  bg-[#F1F8FF] p-4 rounded-[6px] border-[1px] w-full ">
                    <div className="flex flex-col gap-3 md:items-start items-center">
                      <div className="flex gap-2 items-start">
                        <p className="font-bold text-[24px] text-left">
                          {t('contentVideoEditor.voiceSelection.myVoice')}
                        </p>
                        <UpdateTag />
                      </div>
                    </div>

                    {userInfo?.subscriptions?.planId === 3 ? (
                      <>
                        {' '}
                        <div className="grid md:grid-cols-2 grid-cols-1  gap-x-1 gap-y-2 md:pb-4">
                          {userVoiceList?.map((voice, index) => {
                            return (
                              <Controller
                                key={voice.id || index} // 반드시 key 추가!
                                name="userVoiceId"
                                control={control}
                                rules={{
                                  validate: (value) => {
                                    const userVoiceId = watch('userVoiceId')
                                    const voiceId = watch('voiceId')
                                    if (userVoiceId === 0 && voiceId === 0) {
                                      return '❌ 사용자 음성 또는 시스템 음성을 선택해야 합니다.'
                                    }

                                    return true
                                  },
                                }}
                                render={({ field }) => (
                                  <UserVoiceDiv<contentVideoTextTypeFormValues>
                                    {...field}
                                    id={index}
                                    index={index}
                                    setVoiceName={setVoiceName}
                                    setValue={setValue}
                                    watch={watch}
                                    voice={voice}
                                    userVoiceRefetch={userVoiceRefetch}
                                  />
                                )}
                              />
                            )
                          })}
                        </div>
                      </>
                    ) : (
                      <>
                        <div
                          className="flex gap-3 items-center cursor-pointer"
                          onClick={() => {
                            navigate('/price-guide')
                          }}
                        >
                          <PlanTag planName="PREMIUM" />
                          <p>
                            {t(
                              'contentVideoEditor.voiceSelection.createMyVoice'
                            )}{' '}
                            <span className="font-bold">
                              {"'상품 영상 프리미엄 플랜'"}
                            </span>
                            {t('contentVideoEditor.voiceSelection.needUpgrade')}{' '}
                            {`>>`}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                  <hr className=" border-[1px] border-gray-300  w-full" />
                </div>
              </>
            </div>

            <div className="ai-prompt flex flex-col items-start md:min-h-[300px]">
              <p className="font-bold text-[24px] md:mb-[16px]">
                {t('contentVideoEditor.voiceSelection.basicVoice')}
              </p>
              <div className="mb-4">
                <div
                  className={classNames('', {
                    'flex gap-8': !isMobile,
                    'grid grid-cols-2': isMobile,
                  })}
                >
                  {[
                    { cashRate: 1, price: 500, originPrice: 100 },
                    { cashRate: 2, price: 1000, originPrice: 300 },
                    { cashRate: 3, price: 1500, originPrice: 500 },
                    { cashRate: 4, price: 2000, originPrice: 600 },
                  ].map((el) => (
                    <>
                      <span className="text-main flex  gap-1 items-center">
                        {el.cashRate &&
                          new Array(el.cashRate)
                            .fill(1)
                            .map((el) => (
                              <img
                                src="assets/img/cash.png"
                                alt="check_circle"
                                className="w-[11px] h-[13px]"
                              />
                            ))}
                        <s className="text-[#5E6068]">
                          {el.price.toLocaleString()}
                        </s>{' '}
                        {el.originPrice} 캐시
                      </span>
                    </>
                  ))}
                </div>
              </div>
              {isMobile && (
                <p className=" text-main text-left mb-4">
                  {selectedPrompt.introduction}
                </p>
              )}
              <div
                className={classNames('', {
                  'select_box flex gap-4 mb-[16px]': !isMobile,
                  'select_box flex flex-col gap-4 mb-[16px]': isMobile,
                })}
              >
                <div className="flex items-center gap-2">
                  <span>{t('contentVideoEditor.voiceSelection.gender')}</span>{' '}
                  <div
                    className="relative"
                    onMouseLeave={() => {
                      setGenderSelectBoxOpen(false)
                    }}
                  >
                    <button
                      className={classNames('', {
                        'w-[140px] h-[33px] px-4 py-1 border-[1px] border-[#D4D9DF] rounded-[6px] font-[500] text-[14px] text-left':
                          !isMobile,
                        'w-[140px] h-[51px] px-4 py-1 border-[1px] border-[#D4D9DF] rounded-[6px] font-[500] text-left':
                          isMobile,
                      })}
                      value={genderValue}
                      type="button"
                      onClick={() => {
                        setGenderSelectBoxOpen((prev) => !prev)
                      }}
                    >
                      {' '}
                      {genderWords.length > 0
                        ? genderWords.join(', ')
                        : t('contentVideoEditor.voiceSelection.select')}
                    </button>
                    <img
                      src="assets/img/chevron-down.png"
                      className={classNames('', {
                        'w-[16px] h-[16px] absolute right-2 bottom-2':
                          !isMobile,
                        'w-[16px] h-[16px] absolute right-2 bottom-4': isMobile,
                      })}
                      alt=""
                      onClick={() => {
                        setGenderSelectBoxOpen((prev) => !prev)
                      }}
                    />
                    {isGenderSelectBoxOpen && (
                      <div className=" absolute bg-white z-50 w-[120px] h-[110px] shadow-main flex flex-col px-3 py-1  gap-2 justify-center rounded-[10px]">
                        {voiceTags?.genders.map((gender, index) => {
                          return (
                            <>
                              <div
                                className="flex items-center gap-2"
                                key={`voice_tag_${index}`}
                              >
                                <input
                                  id={`gender-${index}`} // 고유한 id
                                  type="checkbox"
                                  className="w-[20px] h-[20px]"
                                  value={gender}
                                  checked={genderWords.includes(gender)}
                                  onChange={() => handleGenderChange(gender)}
                                />
                                <label htmlFor={`gender-${index}`}>
                                  {gender}
                                </label>
                              </div>
                            </>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </div>

                <div
                  className={classNames('flex items-center gap-2', {
                    '': !isMobile,
                  })}
                >
                  <span>{t('contentVideoEditor.voiceSelection.age')}</span>{' '}
                  <div
                    className="relative"
                    onMouseLeave={() => {
                      setAgeSelectBoxOpen(false)
                    }}
                  >
                    <button
                      className={classNames('', {
                        ' w-[179px] h-[33px] px-4 py-1 border-[1px] border-[#D4D9DF] rounded-[6px] font-[500] text-[14px] text-left':
                          !isMobile,
                        'w-[197px] h-[51px] px-4 py-1 border-[1px] border-[#D4D9DF] rounded-[6px] font-[500]  text-left':
                          isMobile,
                      })}
                      value={ageValue}
                      type="button"
                      onClick={() => {
                        setAgeSelectBoxOpen((prev) => !prev)
                      }}
                    >
                      {' '}
                      {ageWords.length > 0
                        ? ageWords.join(', ')
                        : t('contentVideoEditor.voiceSelection.select')}
                    </button>
                    <img
                      src="assets/img/chevron-down.png"
                      className={classNames('', {
                        'w-[16px] h-[16px] absolute right-2 bottom-2':
                          !isMobile,
                        'w-[16px] h-[16px] absolute right-2 bottom-4': isMobile,
                      })}
                      alt=""
                      onClick={() => {
                        setAgeSelectBoxOpen((prev) => !prev)
                      }}
                    />{' '}
                    {isAgeSelectBoxOpen && (
                      <div className=" absolute bg-white z-50 w-[120px] h-[140px] shadow-main flex flex-col px-3 py-1  gap-2 justify-center rounded-[10px] right-0">
                        {voiceTags?.ages.map((age, index) => {
                          return (
                            <label
                              className="w-full flex justify-start gap-2 items-center"
                              key={`voiceTag_ages_${index}`}
                            >
                              <input
                                id={`age-${index}`} // 고유한 id
                                type="checkbox"
                                className="w-[20px] h-[20px]"
                                value={age}
                                checked={ageWords.includes(age)}
                                onChange={() => handleAgeChange(age)}
                              />
                              <span>{age}</span>
                            </label>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-2 gap-y-2 md:pb-4">
                {voiceListValue?.map((voice, index) => {
                  return (
                    <>
                      <Controller
                        name="voiceId"
                        control={control}
                        rules={{
                          validate: (value) => {
                            const userVoiceId = watch('userVoiceId')
                            const voiceId = watch('voiceId')
                            if (userVoiceId === 0 && voiceId === 0) {
                              return '❌ 사용자 음성 또는 시스템 음성을 선택해야 합니다.'
                            }

                            return true
                          },
                        }}
                        render={({ field }) => (
                          <TypeCastVoicePlayer
                            {...field}
                            id={index}
                            index={index}
                            playing={voicePlaying}
                            handleFn={handleVoicePlayButtonClick}
                            checked={watch('voiceId') === voice.id}
                            onChange={() => {
                              setValue('voiceId', voice.id)
                              setValue('userVoiceId', 0)
                              setVoiceName(voice.name)
                            }}
                            label={voice.name}
                            voiceRefs={voiceRefs}
                            src={voice.shortPath}
                            cashRate={voice?.cashRate}
                            imagePath={voice.image ?? ''}
                            service={voice?.service}
                          />
                        )}
                      />
                    </>
                  )
                })}
              </div>
            </div>
            {isMobile && (
              <div
                className={classNames(
                  'bg-white  w-[335px]   flex flex-col   gap-8 z-50 ',
                  {}
                )}
              >
                <button
                  className="bg-main rounded-full w-full text-white font-bold h-[60px]  z-50"
                  onClick={handleListModalOpen}
                  type="button"
                >
                  {t('contentVideoEditor.buttons.confirm')}
                </button>
              </div>
            )}
          </div>
        </div>
        {!isMobile && (
          <div
            className="sticky bottom-0 p-8 shadow-main w-full md:w-[816px] bg-white rounded-b-[20px] 
        before:content-[''] before:absolute before:top-[-10px] before:left-0 
        before:w-full before:h-[10px] before:bg-gradient-to-t before:from-black/10 before:to-transparent z-50"
          >
            <button
              className="bg-main rounded-full w-full text-white font-bold h-[60px]  z-50"
              onClick={handleListModalOpen}
              type="button"
            >
              {t('contentVideoEditor.buttons.confirm')}
            </button>
          </div>
        )}
      </SelectionModal>
    </>
  )
}
